import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LabelingBlock, LabelingSample, LabelingTaskMeta } from "../types";
import { deleteLabelingTask, fetchLabelingTask, fetchLabelingTasks, saveLabelingTask } from "../services/labelingService";
import { API_URL } from "../services/api";

type Viewport = { scale: number; offsetX: number; offsetY: number };
type Mode = "pan" | "draw" | "adjust" | "edit";

const formatDate = (value?: string) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const normalizeDocTypeLocal = (val?: string) =>
  (val || "").toLowerCase().replace(/[-\s]+/g, "_").replace("identity", "identite");

const loadCustomFieldsStorage = (dt: string): string[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem("labeling_custom_fields");
    if (!raw) return [];
    const obj = JSON.parse(raw);
    return Array.isArray(obj[dt]) ? obj[dt] : [];
  } catch (e) {
    return [];
  }
};

const saveCustomFieldsStorage = (dt: string, fields: string[]) => {
  if (typeof window === "undefined") return;
  try {
    const raw = localStorage.getItem("labeling_custom_fields");
    const obj = raw ? JSON.parse(raw) : {};
    obj[dt] = Array.from(new Set(fields));
    localStorage.setItem("labeling_custom_fields", JSON.stringify(obj));
  } catch (e) {
    // ignore
  }
};

const AdminLabelingPage: React.FC = () => {
  const [tasks, setTasks] = useState<LabelingTaskMeta[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [docTypeFilter, setDocTypeFilter] = useState<string | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [sample, setSample] = useState<LabelingSample | null>(null);
  const [loadingSample, setLoadingSample] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customFields, setCustomFields] = useState<string[]>([]);
  const [newField, setNewField] = useState("");

  const [imageBitmap, setImageBitmap] = useState<ImageBitmap | null>(null);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [viewport, setViewport] = useState<Viewport>({ scale: 1, offsetX: 0, offsetY: 0 });
  const [mode, setMode] = useState<Mode>("pan");
  const [isPointerDown, setIsPointerDown] = useState(false);
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(null);
  const [selectedBlock, setSelectedBlock] = useState<number | null>(null);
  const [hoveredBlock, setHoveredBlock] = useState<number | null>(null);
  const [overlayOffset, setOverlayOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [editAction, setEditAction] = useState<
    | null
    | { type: "move"; index: number; start: { x: number; y: number } }
    | { type: "resize"; index: number; start: { x: number; y: number }; corner: "tl" | "tr" | "br" | "bl" }
  >(null);
  const lastEditPos = useRef<{ x: number; y: number } | null>(null);
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const formatFieldLabel = useCallback(
    (field: string) => {
      const dt = (sample?.doc_type || "").toLowerCase();
      const cgLabels: Record<string, string> = {
        registration_number: "(A) Numéro d'immatriculation",
        registration_number_prev: "(A.1) Immat précédente",
        first_registration_date: "(B) Date 1ère immat.",
        brand: "(D.1) Marque",
        type: "(D.2) Type/Var./Vers.",
        type_version: "(D.2.1) Code national (CE)",
        model: "(D.3) Dénomination commerciale",
        vin: "(E) VIN",
        f1PTAC: "(F.1) PTAC techniquement admissible",
        f2PTRA: "(F.2) PTAC en service",
        f3PTRAEnsemble: "(F.3) PTAC ensemble",
        g1PoidsVideNational: "(G.1) Poids à vide national",
        kReceptionType: "(K) N° réception par type",
        genre_j1: "(J.1) Genre national",
        genre: "(J.1) Genre national",
        j2CarrosserieUE: "(J.2) Carrosserie CE",
        j3CarrosserieNational: "(J.3) Carrosserie nationale",
        jCategorieVehicule: "(J) Catégorie CE",
        p1_cylindree: "(P.1) Cylindrée",
        p2_puissance_kw: "(P.2) Puissance (kW)",
        p3_carburant: "(P.3) Carburant / énergie",
        p6_puissance_fiscale: "(P.6) Puissance fiscale",
        s1_places: "(S.1) Places assises",
        s2_places_debout: "(S.2) Places debout",
        u1_niveau_sonore: "(U.1) Niveau sonore",
        u2_regime_moteur: "(U.2) Régime moteur",
        v7_co2: "(V.7) CO₂ (g/km)",
        v9_norme: "(V.9) Classe environnementale CE",
        x1DateProchaineVisiteTechnique: "(X.1) Prochain CT",
        y1TaxeRegionale: "(Y.1) Taxe régionale",
        y2TaxeFormation: "(Y.2) Taxe formation",
        y3TaxeCO2: "(Y.3) Taxe CO₂ / écotaxe",
        y4TaxeVehiculePuissant: "(Y.4) Taxe véhicule puissant",
        y5RedevanceGestion: "(Y.5) Redevance gestion",
        y6TaxeTotale: "(Y.6) Total taxes",
        mileage: "Kilométrage",
        owner: "(C) Titulaire",
        address: "(C) Adresse titulaire",
        postal_code: "(C) CP titulaire",
        city: "(C) Ville titulaire",
      };
      if (dt.includes("grise") || dt.startsWith("carte_grise") || dt === "cg") {
        return cgLabels[field] || field;
      }
      return field;
    },
    [sample?.doc_type],
  );

  const getOverlayMetrics = useCallback(() => {
    const imgW = imageBitmap?.width || naturalSize.width || sample?.page_width || 0;
    const imgH = imageBitmap?.height || naturalSize.height || sample?.page_height || 0;
    const baseW = sample?.page_width || imgW;
    const baseH = sample?.page_height || imgH;
    // Repère direct (pas de rotation)
    const scaleX = baseW ? imgW / baseW : 1;
    const scaleY = baseH ? imgH / baseH : 1;
    const overlayW = imgW;
    const overlayH = imgH;
    return { imgW, imgH, overlayW, overlayH, scaleX, scaleY, baseW, baseH };
  }, [imageBitmap, naturalSize.width, naturalSize.height, sample?.page_width, sample?.page_height]);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);

  const allowedFields = useMemo(() => {
    const dt = normalizeDocTypeLocal(sample?.doc_type);
    const baseFromSample = sample?.allowed_fields && sample.allowed_fields.length ? sample.allowed_fields : [];
    const isCarteGrise = dt.includes("grise") || dt.startsWith("carte_grise") || dt === "cg";
    const isJustif = dt.includes("justif") || dt === "address";
    const isCt = dt.includes("controle_technique") || dt === "ct" || dt === "pv_ct";
    const isId =
      dt.includes("ident") ||
      dt.includes("cni") ||
      dt.includes("id") ||
      dt.includes("passeport") ||
      dt.includes("passport") ||
      dt.includes("resident") ||
      dt.includes("titre_sejour") ||
      dt.includes("titre_de_sejour");

    let preset: string[] = [];
    if (isCarteGrise) {
      preset = [
        "registration_number",
        "vin",
        "first_registration_date",
        "brand",
        "type",
        "type_version",
        "model",
        "mileage",
        "genre_j1",
        "owner",
        "address",
        "postal_code",
        "city",
      ];
    } else if (isJustif) {
      preset = ["name", "address", "city_cp", "document_date", "issuer_name", "document_reference"];
    } else if (isCt) {
      preset = [
        "registration_number",
        "vin",
        "ct_date",
        "valid_until",
        "result",
        "pv_number",
        "center_name",
        "center_siret",
        "mileage_km",
        "defects",
        "defects_minor",
        "defects_major",
        "defects_critical",
        "observations",
      ];
    } else if (isId) {
      preset = [
        "last_name",
        "first_name",
        "birth_date",
        "birth_place",
        "gender",
        "nationality",
        "document_number",
        "issue_date",
        "expiration_date",
        "mrz_l1",
        "mrz_l2",
      ];
    } else {
      preset = [
        "last_name",
        "first_name",
        "birth_date",
        "birth_place",
        "gender",
        "nationality",
        "document_number",
        "issue_date",
        "expiration_date",
      ];
    }
    return Array.from(new Set([...preset, ...baseFromSample, ...customFields].filter(Boolean)));
  }, [sample, customFields]);

  const blocksToRender = useMemo(() => {
    if (!sample) return [];
    return showOnlySelected && selectedBlock !== null
      ? sample.blocks.map((b, idx) => ({ block: b, idx })).filter((b) => b.idx === selectedBlock)
      : sample.blocks.map((b, idx) => ({ block: b, idx }));
  }, [sample, showOnlySelected, selectedBlock]);

  const resolveImageUrl = (url?: string) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${API_URL}${url}`;
  };

  const loadTasks = useCallback(async () => {
    setTasksLoading(true);
    setError(null);
    try {
      const response = await fetchLabelingTasks();
      setTasks(response.tasks);
      if (!selectedTaskId && response.tasks.length) {
        setSelectedTaskId(response.tasks[0].id);
      }
    } catch (err) {
      console.error(err);
      setError("Impossible de charger les tâches");
    } finally {
      setTasksLoading(false);
    }
  }, [selectedTaskId]);

  const loadSample = useCallback(
    async (taskId: string | null) => {
      if (!taskId) {
        setSample(null);
        setImageBitmap(null);
        setCustomFields([]);
        return;
      }
      setLoadingSample(true);
      setError(null);
      setSelectedBlock(null);
      try {
        const data = await fetchLabelingTask(taskId);
        setSample(data);
        const dt = normalizeDocTypeLocal(data.doc_type);
        const stored = loadCustomFieldsStorage(dt);
        const inferredCustom = Array.from(
          new Set([
            ...stored,
            ...(data.allowed_fields || []),
            ...(data.blocks || []).map((b: any) => b.field).filter((f: any) => f),
          ]),
        );
        setCustomFields(inferredCustom);
        // charge l'image
        if (data.image_url) {
          const url = resolveImageUrl(data.image_url);
          try {
            const resp = await fetch(url);
            const blob = await resp.blob();
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const bmp: ImageBitmap = await createImageBitmap(blob, { imageOrientation: "none" });
            setImageBitmap(bmp);
            setNaturalSize({ width: bmp.width, height: bmp.height });
            setViewport({ scale: 1, offsetX: 0, offsetY: 0 });
          } catch (e) {
            console.error("Erreur chargement image", e);
            setImageBitmap(null);
          }
        }
      } catch (err) {
        console.error(err);
        setError("Impossible de charger la tâche");
      } finally {
        setLoadingSample(false);
      }
    },
    [],
  );

  useEffect(() => {
    void loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    void loadSample(selectedTaskId);
  }, [selectedTaskId, loadSample]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.scale(1, 1); // already set by resize
    // apply viewport
    ctx.translate(viewport.offsetX, viewport.offsetY);
    ctx.scale(viewport.scale, viewport.scale);

    const { imgW, imgH, scaleX, scaleY, baseH } = getOverlayMetrics();

    // draw image
    if (imageBitmap) {
      ctx.drawImage(imageBitmap, 0, 0, imageBitmap.width, imageBitmap.height);
    }

    // draw blocks (repère direct, pas de rotation)
    if (sample) {
      sample.blocks.forEach((block, idx) => {
        const xs = block.box.map((p) => p[0]);
        const ys = block.box.map((p) => p[1]);
        const x = Math.min(...xs);
        const y = Math.min(...ys);
        const w = Math.max(...xs) - x;
        const h = Math.max(...ys) - y;

        const mappedX = x * scaleX + overlayOffset.x;
        const mappedY = y * scaleY + overlayOffset.y;
        const mappedW = w * scaleX;
        const mappedH = h * scaleY;

        const isHovered = hoveredBlock === idx;
        const stroke = isHovered ? "#f59e0b" : block.ignored ? "rgba(239,68,68,0.5)" : "#ef4444";
        const fill = block.ignored ? "rgba(239,68,68,0.12)" : "rgba(239,68,68,0.08)";
        ctx.fillStyle = fill;
        ctx.strokeStyle = stroke;
        ctx.lineWidth = selectedBlock === idx ? 3 / viewport.scale : 2 / viewport.scale;
        ctx.beginPath();
        ctx.rect(mappedX, mappedY, mappedW, mappedH);
        ctx.fill();
        ctx.stroke();
        const label = block.field || block.predicted_field || "";
        if (label) {
          ctx.fillStyle = stroke;
          ctx.font = `${12 / viewport.scale}px Inter, sans-serif`;
          ctx.fillText(label, mappedX + 4 / viewport.scale, mappedY + 14 / viewport.scale);
        }
      });
    }
    ctx.restore();
    // draw FPS/info if needed (not now)
  }, [imageBitmap, sample, viewport, selectedBlock]);

  const scheduleDraw = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => draw());
  }, [draw]);

  // Centre et borne l'offset du calque quand on charge une nouvelle image
  useEffect(() => {
    const { imgW, imgH, overlayW, overlayH } = getOverlayMetrics();
    if (!imgW || !imgH) return;
    const centeredX = (imgW - overlayW) / 2;
    const centeredY = (imgH - overlayH) / 2;
    const minX = imgW - overlayW;
    const minY = imgH - overlayH;
    const maxX = 0;
    const maxY = 0;
    setOverlayOffset({
      x: Math.min(maxX, Math.max(minX, centeredX)),
      y: Math.min(maxY, Math.max(minY, centeredY)),
    });
  }, [getOverlayMetrics]);

  // Resize canvas to container + DPR
  useEffect(() => {
    const resize = () => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;
      const dpr = window.devicePixelRatio || 1;
      const { clientWidth, clientHeight } = container;
      canvas.width = clientWidth * dpr;
      canvas.height = clientHeight * dpr;
      canvas.style.width = `${clientWidth}px`;
      canvas.style.height = `${clientHeight}px`;
      const ctx = canvas.getContext("2d");
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      scheduleDraw();
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [scheduleDraw]);

  useEffect(() => {
    scheduleDraw();
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [scheduleDraw]);

  const screenToImage = useCallback(
    (clientX: number, clientY: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0 };
      const rect = canvas.getBoundingClientRect();
      const { scaleX, scaleY } = getOverlayMetrics();
      // repère canvas (après viewport)
      const cx = (clientX - rect.left - viewport.offsetX) / viewport.scale;
      const cy = (clientY - rect.top - viewport.offsetY) / viewport.scale;
      const x = (cx - overlayOffset.x) / scaleX;
      const y = (cy - overlayOffset.y) / scaleY;
      return { x, y };
    },
    [viewport, getOverlayMetrics, overlayOffset],
  );

  const handleWheel = (e: React.WheelEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    // point sous la souris dans le repère canvas actuel (avant rotation)
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const delta = e.deltaY < 0 ? 1.1 : 0.9;
    setViewport((v) => {
      const newScale = Math.min(Math.max(v.scale * delta, 0.2), 8);
      // maintenir le point sous la souris
      const newOffsetX = cx - (cx - v.offsetX) * (newScale / v.scale);
      const newOffsetY = cy - (cy - v.offsetY) * (newScale / v.scale);
      return { scale: newScale, offsetX: newOffsetX, offsetY: newOffsetY };
    });
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!sample) return;
    setIsPointerDown(true);
    const pos = screenToImage(e.clientX, e.clientY);
    if (mode === "draw") {
      setDrawStart(pos);
    } else if (mode === "adjust") {
      setDrawStart(pos);
    } else if (mode === "edit") {
      const hit = hitTest(pos.x, pos.y);
      if (hit !== null) {
        setSelectedBlock(hit);
        const rect = getBlockRect(sample.blocks[hit]);
        const handle = hitHandle(pos.x, pos.y, rect);
        if (handle) {
          setEditAction({ type: "resize", index: hit, start: pos, corner: handle });
        } else {
          setEditAction({ type: "move", index: hit, start: pos });
        }
        lastEditPos.current = pos;
      } else {
        setEditAction(null);
        lastEditPos.current = null;
      }
    } else {
      setDrawStart(null);
    }
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isPointerDown) {
      // hover highlight
      const pos = screenToImage(e.clientX, e.clientY);
      const hit = hitTest(pos.x, pos.y);
      setHoveredBlock(hit);
      return;
    }
    if (mode === "pan" && drawStart === null) {
      setViewport((v) => ({
        ...v,
        offsetX: v.offsetX + e.movementX,
        offsetY: v.offsetY + e.movementY,
      }));
    } else if (mode === "adjust" && drawStart) {
      // déplace uniquement le calque blocs
      const { imgW, imgH, overlayW, overlayH } = getOverlayMetrics();
      setOverlayOffset((o) => {
        const nextX = o.x + e.movementX / viewport.scale;
        const nextY = o.y + e.movementY / viewport.scale;
        const minX = imgW - overlayW;
        const minY = imgH - overlayH;
        const maxX = 0;
        const maxY = 0;
        return {
          x: Math.min(maxX, Math.max(minX, nextX)),
          y: Math.min(maxY, Math.max(minY, nextY)),
        };
      });
      scheduleDraw();
    } else if (mode === "edit" && editAction) {
      const newPos = screenToImage(e.clientX, e.clientY);
      const prevPos = lastEditPos.current || newPos;
      const deltaX = newPos.x - prevPos.x;
      const deltaY = newPos.y - prevPos.y;
      lastEditPos.current = newPos;
      if (editAction.type === "move") {
        moveBlock(editAction.index, deltaX, deltaY);
      } else if (editAction.type === "resize") {
        resizeBlock(editAction.index, deltaX, deltaY, editAction.corner);
      }
      scheduleDraw();
    }
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    setIsPointerDown(false);
    const pos = screenToImage(e.clientX, e.clientY);
    if (!sample) return;

    if (mode === "draw" && drawStart) {
      const x1 = drawStart.x;
      const y1 = drawStart.y;
      const x2 = pos.x;
      const y2 = pos.y;
      const x = Math.min(x1, x2);
      const y = Math.min(y1, y2);
      const w = Math.abs(x2 - x1);
      const h = Math.abs(y2 - y1);
      if (w > 5 && h > 5) {
        const newBlock: LabelingBlock = {
          text: "",
          conf: 0,
          box: [
            [x, y],
            [x + w, y],
            [x + w, y + h],
            [x, y + h],
          ],
          field: "",
          predicted_field: "",
          ignored: false,
        };
        setSample((prev) => (prev ? { ...prev, blocks: [...prev.blocks, newBlock] } : prev));
        setSelectedBlock(sample.blocks.length);
      }
      setDrawStart(null);
    } else if (mode === "pan") {
      // sélection bloc
      const hit = hitTest(pos.x, pos.y);
      setSelectedBlock(hit);
    } else if (mode === "adjust" && drawStart) {
      // fin de déplacement calque, rien d'autre
      setDrawStart(null);
    } else if (mode === "edit") {
      setEditAction(null);
      lastEditPos.current = null;
    }
  };

  const hitTest = (x: number, y: number): number | null => {
    if (!sample) return null;
    for (let i = sample.blocks.length - 1; i >= 0; i--) {
      const b = sample.blocks[i];
      const xs = b.box.map((p) => p[0]);
      const ys = b.box.map((p) => p[1]);
      const bx = Math.min(...xs);
      const by = Math.min(...ys);
      const bw = Math.max(...xs) - bx;
      const bh = Math.max(...ys) - by;
      if (x >= bx && x <= bx + bw && y >= by && y <= by + bh) return i;
    }
    return null;
  };

  const getBlockRect = (block: LabelingBlock) => {
    const xs = block.box.map((p) => p[0]);
    const ys = block.box.map((p) => p[1]);
    const x = Math.min(...xs);
    const y = Math.min(...ys);
    const w = Math.max(...xs) - x;
    const h = Math.max(...ys) - y;
    return { x, y, w, h };
  };

  const hitHandle = (x: number, y: number, rect: { x: number; y: number; w: number; h: number }) => {
    const s = 10; // taille handle
    const { x: bx, y: by, w, h } = rect;
    const handles: { corner: "tl" | "tr" | "br" | "bl"; hx: number; hy: number }[] = [
      { corner: "tl", hx: bx, hy: by },
      { corner: "tr", hx: bx + w, hy: by },
      { corner: "br", hx: bx + w, hy: by + h },
      { corner: "bl", hx: bx, hy: by + h },
    ];
    return (
      handles.find((h1) => Math.abs(x - h1.hx) <= s && Math.abs(y - h1.hy) <= s)?.corner || null
    );
  };

  const moveBlock = (index: number, dx: number, dy: number) => {
    setSample((prev) => {
      if (!prev) return prev;
      const blocks = [...prev.blocks];
      const b = blocks[index];
      const newBox = b.box.map(([px, py]) => [px + dx, py + dy]);
      blocks[index] = { ...b, box: newBox as number[][] };
      return { ...prev, blocks };
    });
  };

  const resizeBlock = (index: number, dx: number, dy: number, corner: "tl" | "tr" | "br" | "bl") => {
    setSample((prev) => {
      if (!prev) return prev;
      const blocks = [...prev.blocks];
      const b = blocks[index];
      const { x, y, w, h } = getBlockRect(b);
      let nx = x;
      let ny = y;
      let nw = w;
      let nh = h;
      const minSize = 5;
      if (corner === "tl") {
        nx = x + dx;
        ny = y + dy;
        nw = w - dx;
        nh = h - dy;
      } else if (corner === "tr") {
        ny = y + dy;
        nw = w + dx;
        nh = h - dy;
      } else if (corner === "br") {
        nw = w + dx;
        nh = h + dy;
      } else if (corner === "bl") {
        nx = x + dx;
        nw = w - dx;
        nh = h + dy;
      }
      if (nw < minSize || nh < minSize) return prev;
      const newBox: number[][] = [
        [nx, ny],
        [nx + nw, ny],
        [nx + nw, ny + nh],
        [nx, ny + nh],
      ];
      blocks[index] = { ...b, box: newBox };
      return { ...prev, blocks };
    });
  };

  const scaleXFromMetrics = () => getOverlayMetrics().scaleX || 1;
  const scaleYFromMetrics = () => getOverlayMetrics().scaleY || 1;

  const handleFieldChange = (index: number, value: string) => {
    setSample((prev) => {
      if (!prev) return prev;
      const blocks = [...prev.blocks];
      blocks[index] = { ...blocks[index], field: value };
      return { ...prev, blocks };
    });
  };

  const handleTextChange = (index: number, value: string) => {
    setSample((prev) => {
      if (!prev) return prev;
      const blocks = [...prev.blocks];
      blocks[index] = { ...blocks[index], text: value };
      return { ...prev, blocks };
    });
  };

  const handleIgnoreToggle = (index: number) => {
    setSample((prev) => {
      if (!prev) return prev;
      const blocks = [...prev.blocks];
      blocks[index] = { ...blocks[index], ignored: !blocks[index].ignored };
      return { ...prev, blocks };
    });
  };

  const handleDeleteBlock = (index: number) => {
    setSample((prev) => {
      if (!prev) return prev;
      const blocks = prev.blocks.filter((_, i) => i !== index);
      return { ...prev, blocks };
    });
    setSelectedBlock(null);
  };

  const handleApplyPredictedField = (index: number, value?: string) => {
    if (!value) return;
    handleFieldChange(index, value);
  };

  const handleSave = async (goNext = false) => {
    if (!sample || !selectedTaskId) return;
    setSaving(true);
    setError(null);
    try {
      if (customFields.length) {
        sample.allowed_fields = Array.from(new Set([...(sample.allowed_fields || []), ...customFields]));
      }
      await saveLabelingTask(selectedTaskId, sample);
      await loadTasks();
      if (goNext) {
        const idx = tasks.findIndex((t) => t.id === selectedTaskId);
        const next = tasks[idx + 1];
        setSelectedTaskId(next ? next.id : selectedTaskId);
      }
    } catch (err) {
      console.error(err);
      setError("Sauvegarde impossible");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTask = async () => {
    if (!selectedTaskId) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteLabelingTask(selectedTaskId);
      const next = tasks.filter((t) => t.id !== selectedTaskId);
      setTasks(next);
      setSelectedTaskId(next.length ? next[0].id : null);
      setSample(null);
      setCustomFields([]);
    } catch (err) {
      console.error(err);
      setError("Suppression impossible");
    } finally {
      setDeleting(false);
    }
  };

  const resetView = () => {
    setViewport({ scale: 1, offsetX: 0, offsetY: 0 });
  };

  return (
    <div className="h-screen w-screen bg-slate-900 text-slate-100 flex">
      <aside className="w-72 border-r border-slate-800 p-3 overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold">Tâches</div>
          {tasksLoading && <span className="text-xs text-slate-400">...</span>}
        </div>
        {error && <div className="text-red-400 text-xs mb-2">{error}</div>}
        <div className="mb-2 text-xs text-slate-300">
          <label className="block mb-1 text-slate-400">Filtrer par doc-type</label>
          <select
            value={docTypeFilter || ""}
            onChange={(e) => setDocTypeFilter(e.target.value || null)}
            className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-white"
          >
            <option value="">Tous</option>
            {[...new Set(tasks.map((t) => (t.doc_type || "").toLowerCase().replace("identity", "identite")))]
              .filter(Boolean)
              .sort()
              .map((dt) => (
                <option key={dt} value={dt}>
                  {dt}
                </option>
              ))}
          </select>
        </div>
        <div className="space-y-2">
          {tasks
            .filter((task) => {
              const dt = (task.doc_type || "").toLowerCase().replace("identity", "identite");
              return !docTypeFilter || dt === docTypeFilter;
            })
            .map((task) => {
              const dt = (task.doc_type || "").toLowerCase().replace("identity", "identite");
              return (
                <button
                  key={task.id}
                  onClick={() => setSelectedTaskId(task.id)}
                  className={`w-full text-left p-2 rounded border ${
                    selectedTaskId === task.id ? "border-blue-500 bg-slate-800" : "border-slate-800 bg-slate-900"
                  } hover:bg-slate-800`}
                >
                  <div className="text-sm font-semibold truncate">{task.filename}</div>
                  <div className="text-[11px] text-slate-400">
                    {dt || "-"} • {task.blocks} blocs
                  </div>
                  {task.updated_at && <div className="text-[10px] text-slate-500">{formatDate(task.updated_at)}</div>}
                </button>
              );
            })}
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0">
        <header className="p-3 border-b border-slate-800 flex items-center gap-3">
          <div className="text-lg font-semibold flex-1">Annotation OCR</div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMode("pan")}
              className={`px-3 py-2 rounded text-sm border ${mode === "pan" ? "bg-blue-700 border-blue-500" : "bg-slate-800 border-slate-700"}`}
            >
              Déplacer vue
            </button>
            <button
              onClick={() => setMode("draw")}
              className={`px-3 py-2 rounded text-sm border ${mode === "draw" ? "bg-amber-600 border-amber-500" : "bg-slate-800 border-slate-700"}`}
            >
              Ajouter bloc
            </button>
            <button
              onClick={() => setMode("adjust")}
              className={`px-3 py-2 rounded text-sm border ${mode === "adjust" ? "bg-emerald-600 border-emerald-500" : "bg-slate-800 border-slate-700"}`}
            >
              Ajuster calque
            </button>
            <button
              onClick={() => setMode("edit")}
              className={`px-3 py-2 rounded text-sm border ${mode === "edit" ? "bg-violet-700 border-violet-500" : "bg-slate-800 border-slate-700"}`}
            >
              Éditer blocs
            </button>
            <button onClick={resetView} className="px-3 py-2 rounded text-sm bg-slate-800 border border-slate-700">
              Réinitialiser vue
            </button>
          </div>
          <button
            onClick={handleDeleteTask}
            disabled={!sample || deleting}
            className="px-3 py-2 bg-red-600 hover:bg-red-500 rounded text-sm disabled:opacity-60"
          >
            Supprimer
          </button>
          <button
            onClick={() => handleSave(false)}
            disabled={!sample || saving}
            className="px-3 py-2 bg-blue-600 hover:bg-blue-500 rounded text-sm disabled:opacity-60"
          >
            {saving ? "Enregistrement..." : "Enregistrer"}
          </button>
          <button
            onClick={() => handleSave(true)}
            disabled={!sample || saving}
            className="px-3 py-2 bg-emerald-600 hover:bg-emerald-500 rounded text-sm disabled:opacity-60"
          >
            {saving ? "..." : "Enregistrer & suivant"}
          </button>
        </header>

        <div className="flex-1 grid grid-cols-[2fr_1fr] gap-4 p-4 overflow-hidden">
          <div className="relative border border-slate-800 rounded bg-slate-900 flex items-center justify-center overflow-hidden">
              {sample?.image_url ? (
                <div ref={containerRef} className="w-full h-full relative">
                  <canvas
                    ref={canvasRef}
                    className="w-full h-full cursor-crosshair"
                  onWheel={handleWheel}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerLeave={() => setIsPointerDown(false)}
                />
                {mode === "draw" && (
                  <div className="absolute top-2 left-2 px-2 py-1 bg-amber-500 text-xs text-black rounded">
                    Mode dessin : cliquer-glisser pour créer un bloc
                  </div>
                )}
                </div>
              ) : (
                <div className="text-sm text-slate-400 p-4">Aucune image pour cette tâche.</div>
              )}
            </div>

          <div className="overflow-y-auto border border-slate-800 rounded p-3 bg-slate-900">
            {loadingSample && <div className="text-sm text-slate-400">Chargement...</div>}
            {sample ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-xs text-slate-300">
                  <label className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      className="accent-blue-500"
                      checked={showOnlySelected}
                      onChange={(e) => setShowOnlySelected(e.target.checked)}
                    />
                    N’afficher que le bloc sélectionné
                  </label>
                  {showOnlySelected && selectedBlock === null && (
                    <span className="text-amber-400">Sélectionne un bloc pour le voir.</span>
                  )}
                </div>

                {blocksToRender.map(({ block, idx }) => {
                  const isSelected = selectedBlock === idx;
                  const predicted = block.predicted_field;
                  const candidates = block.predicted_candidates || [];
                  const options = [...allowedFields];
                  if (predicted && !options.includes(predicted)) options.unshift(predicted);
                  return (
                    <div
                      key={idx}
                      className={`p-2 border rounded bg-slate-800/60 cursor-pointer ${
                        isSelected ? "border-blue-400" : "border-slate-800"
                      }`}
                      onClick={() => setSelectedBlock(idx)}
                      onMouseEnter={() => setHoveredBlock(idx)}
                      onMouseLeave={() => setHoveredBlock(null)}
                    >
                      <div className="flex justify-between items-center mb-1">
                        <div className="text-xs text-slate-400 truncate">{block.text || "<vide>"}</div>
                        <div className="text-[11px] text-slate-500">conf {block.conf?.toFixed(2) ?? "-"}</div>
                      </div>
                      {predicted && (
                        <div className="text-[11px] text-emerald-400 mb-1 flex items-center gap-2">
                          Prédiction : {predicted}
                          <button
                            className="px-2 py-1 bg-emerald-700 rounded text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApplyPredictedField(idx, predicted);
                            }}
                          >
                            Appliquer
                          </button>
                        </div>
                      )}
                      {candidates.length > 0 && (
                        <div className="text-[10px] text-slate-400 mb-1">
                          Suggestions : {candidates.join(", ")}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-[11px] mb-1">
                        <label className="text-slate-300">Champ :</label>
                        <select
                          value={block.field || ""}
                          onChange={(e) => handleFieldChange(idx, e.target.value)}
                          className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm flex-1"
                        >
                          <option value="">(non défini)</option>
                          {options.map((f) => (
                            <option key={f} value={f}>
                              {formatFieldLabel(f)}
                            </option>
                          ))}
                        </select>
                        <label className="flex items-center gap-1 text-slate-300">
                          <input type="checkbox" checked={!!block.ignored} onChange={() => handleIgnoreToggle(idx)} />
                          Ignorer
                        </label>
                      </div>
                      <textarea
                        value={block.text || ""}
                        onChange={(e) => handleTextChange(idx, e.target.value)}
                        className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm"
                        rows={2}
                      />
                      <div className="flex justify-between mt-2 text-[11px]">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteBlock(idx);
                          }}
                          className="px-2 py-1 bg-slate-800 border border-slate-700 rounded text-red-300"
                        >
                          Supprimer bloc
                        </button>
                      <div className="text-slate-500">#{idx + 1}</div>
                    </div>
                  </div>
                );
              })}

                <div className="p-2 border border-slate-800 rounded bg-slate-800/50 text-xs text-slate-300">
                  <div className="font-semibold mb-1">Ajouter un champ</div>
                  <div className="flex gap-2">
                    <input
                      value={newField}
                      onChange={(e) => setNewField(e.target.value)}
                      placeholder="ex: defects_pollution"
                      className="flex-1 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm"
                    />
                  <button
                    className="px-3 py-1 bg-emerald-600 rounded text-white text-xs"
                    onClick={() => {
                      const nf = newField.trim();
                      if (!nf) return;
                      const dt = normalizeDocTypeLocal(sample?.doc_type);
                      setCustomFields((prev) => {
                        const updated = Array.from(new Set([...prev, nf]));
                        saveCustomFieldsStorage(dt, updated);
                        return updated;
                      });
                      setSample((prev) =>
                        prev
                          ? {
                              ...prev,
                              allowed_fields: Array.from(new Set([...(prev.allowed_fields || []), nf])),
                            }
                          : prev,
                      );
                      setNewField("");
                    }}
                  >
                    Ajouter
                  </button>
                  </div>
                  {customFields.length > 0 && (
                    <div className="mt-1 text-[11px] text-slate-400">
                      Champs perso : {customFields.join(", ")}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-400">Sélectionne une tâche</div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminLabelingPage;
